import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface BankDetailsContextType {
  bankDetails: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchBankDetails: (page?: number, perPage?: number) => Promise<void>;
  addBankDetail: (bank: any) => Promise<void>;
  editBankDetail: (bank: any) => Promise<void>;
  deleteBankDetail: (id: number | string) => Promise<void>;
}

export const BankDetailsContext = createContext<BankDetailsContextType | undefined>(undefined);

interface BankDetailsProviderProps {
  children: ReactNode;
}

export const useBankDetailsContext = (): BankDetailsContextType => {
  const context = useContext(BankDetailsContext);
  if (!context) {
    throw new Error("useBankDetailsContext must be used within BankDetailsProvider");
  }
  return context;
};

const BankDetailsProvider: React.FC<BankDetailsProviderProps> = ({ children }) => {
  const [bankDetails, setBankDetails] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all bank details with pagination
  const fetchBankDetails = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/bank-details?${params.toString()}`);
      
      // Handle paginated response structure
      let bankDetailsData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          bankDetailsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          bankDetailsData = responseData.data;
          paginationData = responseData.pagination;
        }
      } else if (Array.isArray(responseData?.data)) {
        bankDetailsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        bankDetailsData = responseData;
      }
      
      setBankDetails(Array.isArray(bankDetailsData) ? bankDetailsData : []);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch bank details:", error);
      setBankDetails([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new bank detail
  const addBankDetail = async (bank: any): Promise<void> => {
    try {
      const response = await api.post("/bank-details", bank);
      setBankDetails((prev) => [...prev, response.data?.data || response.data]);
    } catch (error) {
      console.error("Failed to add bank detail:", error);
      throw error;
    }
  };

  // Edit an existing bank detail
  const editBankDetail = async (bank: any): Promise<void> => {
    try {
      const response = await api.put(`/bank-details/${bank.id}`, bank);
      setBankDetails((prev) =>
        prev.map((item) => (item.id === bank.id ? (response.data?.data || response.data) : item))
      );
    } catch (error) {
      console.error("Failed to edit bank detail:", error);
      throw error;
    }
  };

  // Delete a bank detail
  const deleteBankDetail = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/bank-details/${id}`);
      setBankDetails((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete bank detail:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchBankDetails(1, 15);
  }, [fetchBankDetails]);

  return (
    <BankDetailsContext.Provider
      value={{
        bankDetails,
        pagination,
        loading,
        fetchBankDetails,
        addBankDetail,
        editBankDetail,
        deleteBankDetail,
      }}
    >
      {children}
    </BankDetailsContext.Provider>
  );
};

export default BankDetailsProvider;
